import {BaseQueryFn, FetchArgs, FetchBaseQueryError, fetchBaseQuery} from '@reduxjs/toolkit/query/react';

import {LogInType} from 'app/types/Shared';
import {RootState} from '../store';

/**
 * Determines whether a user is an internal Takeda member or an external/CRO guest
 * by inspecting Entra security groups in the decoded ID token claims.
 *
 * Internal users are identified by membership in groups specified in REACT_APP_ENTRA_INTERNAL_GROUP_IDS.
 * This function parses the environment variable and checks if any of the user's groups match.
 * If no internal groups are configured, all users default to External.
 */
export const extractOrganizationFromToken = (claims: Record<string, unknown>): LogInType => {
  console.log('[extractOrganizationFromToken] ===== CALLED ===== with claims:', claims);

  // Parse internal group IDs from environment variable (comma-separated)
  const internalGroupIdsEnv = process.env.REACT_APP_ENTRA_INTERNAL_GROUP_IDS || '';
  console.log('[extractOrganizationFromToken] RAW ENV REACT_APP_ENTRA_INTERNAL_GROUP_IDS:', internalGroupIdsEnv);

  const internalGroupIds = internalGroupIdsEnv
    .split(',')
    .map((id) => id.trim().toLowerCase())
    .filter(Boolean);

  console.log('[extractOrganizationFromToken] Parsed internal group IDs:', internalGroupIds);
  console.log('[extractOrganizationFromToken] Number of configured groups:', internalGroupIds.length);

  if (internalGroupIds.length === 0) {
    console.warn(
      '[extractOrganizationFromToken] WARNING: REACT_APP_ENTRA_INTERNAL_GROUP_IDS is not configured; ' +
        'all authenticated users will be treated as External.',
    );
    return LogInType.External;
  }

  // Extract groups claim from token (array of group object IDs)
  const userGroups = Array.isArray(claims.groups)
    ? (claims.groups as string[]).map((g) => String(g).toLowerCase())
    : [];
  console.log('[extractOrganizationFromToken] User groups from token:', userGroups);
  console.log('[extractOrganizationFromToken] Number of user groups:', userGroups.length);

  // Check if user belongs to any internal group
  const isInternal = internalGroupIds.some((groupId) => {
    const match = userGroups.includes(groupId);
    console.log(`[extractOrganizationFromToken] Checking group "${groupId}" against user groups: ${match}`);
    return match;
  });

  const userType = isInternal ? LogInType.Internal : LogInType.External;
  console.log('[extractOrganizationFromToken] ===== RESULT: detected userType:', userType, '=====');

  return userType;
};

/**
 * DEPRECATED: Kept for backward compatibility. Use extractOrganizationFromToken instead.
 * Determines user type based on the `idp` claim (this was the old Okta-based approach).
 */
export const determineUserTypeFromClaims = (claims: Record<string, unknown>): LogInType => {
  // For now, delegate to the new group-based function
  return extractOrganizationFromToken(claims);
};

const processToken = (token: string) => {
  const parts = token.split('.');
  if (parts.length !== 3) {
    throw new Error('Invalid token');
  }

  const decodedToken = JSON.parse(atob(parts[1]));
  const tokenVersion = decodedToken.ver; // Token version claim

  // Validate token version - reject v1 tokens
  if (tokenVersion !== '2.0') {
    console.error(`Invalid token version detected: ${tokenVersion}. Expected v2.0 token.`);
    throw new Error(`Token version ${tokenVersion} not supported. Please use v2.0 tokens.`);
  }
};

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_BASEURL || 'http://localhost:3500',
  prepareHeaders: (headers, {getState}) => {
    const authToken = (getState() as RootState).auth.token;
    if (authToken && authToken.idToken) {
      // Use idToken for all API calls (OIDC standard for own API)
      headers.set('Authorization', `Bearer ${authToken.idToken}`);
      //attach user type to header for content restrictions
      processToken(authToken.idToken);
      // Use the backend organization value when available (set from the Okta era).
      // Fall back to the auto-detected userType so the header is always present.
      // IMPORTANT: RequesterType header is ALWAYS set for both Internal and External users
      // because the backend needs it to properly filter content and apply access policies.
      const organization = (getState() as RootState).user.user?.organization;
      const userType = (getState() as RootState).user.userType;
      const requesterType = organization ?? userType;
      console.log('[auth.baseQuery] RequesterType resolution');
      console.log('  → organization:', organization);
      console.log('  → userType:', userType);
      console.log('  → resolved requesterType:', requesterType);
      console.log('  → All user state:', (getState() as RootState).user);

      if (requesterType) {
        headers.set('RequesterType', requesterType);
        console.log('[auth.baseQuery] ✅ RequesterType header SET to:', requesterType);
      } else {
        console.warn('[auth.baseQuery] ❌ RequesterType header NOT set — requesterType is null/undefined');
      }
    }
    return headers;
  },
});

const baseQueryWithMiddleware: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const result = await baseQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    window.location.reload();
  }
  return result;
};

export {baseQueryWithMiddleware, baseQuery};
